import { api } from '@/lib/apiClient'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { Head, router } from '@inertiajs/react'
import { useMutation, useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { Button, message, Typography } from 'antd'
import { useEffect, useState } from 'react'

import AdminLayout from '@/components/layouts/AdminLayout'
import PurgeDataSidebar from './components/PurgeDataSidebar'
import type { PurgeDataItem } from './components/PurgeDataSidebar'
import { DeleteEventModal } from './components/DeleteEventModal'
import { ActionCard } from './components/ActionCard'
import { EventDetailsSection } from '@/components/events/EventDetailsSection'

const { Title } = Typography

interface EventDetails {
  id: number
  name: string
  slug: string | null
  start_date: string
  end_date: string
  client_name: string
  status: 'draft' | 'live' | 'archived' | 'purged' | 'deleted'
  allow_public_registration: boolean
  allow_attendee_app_access: boolean
  description?: string
  location?: string
  theme: string
  logo?: string | null
  registered?: number
  pending_approval?: number
  approved?: number
  scan_points_last_scanned_at?: string | null
  scan_points?: Array<{
    id: number
    name: string
    kind: 'event_check_in' | 'custom' | 'session_link'
    count: number
  }>
}

interface EventDataOperation {
  id: number
  operation_type: 'purge' | 'delete'
  status: 'pending' | 'processing' | 'completed' | 'failed'
  result: Record<string, unknown>
  error_message?: string | null
}

interface ShowEventProps {
  event: EventDetails
  purgeFields: PurgeDataItem[]
  activeOperation?: EventDataOperation | null
}

export default function ShowEvent({
  event: initialEvent,
  purgeFields,
  activeOperation,
}: ShowEventProps) {
  const [deleteModalVisible, setDeleteModalVisible] = useState(false)
  const [purgeSidebarOpen, setPurgeSidebarOpen] = useState(false)
  const [activeOperationId, setActiveOperationId] = useState<number | null>(
    activeOperation?.id || null
  )

  // Use the initial event data from props, optionally refetch if needed
  const event = initialEvent

  const operationQuery = useQuery({
    queryKey: ['event-data-operation', activeOperationId],
    queryFn: () =>
      api.get<EventDataOperation>(`/admin/event_data_operations/${activeOperationId}`),
    enabled: activeOperationId !== null,
    refetchInterval: 1500,
  })

  useEffect(() => {
    const operation = operationQuery.data
    if (!operation) return

    if (operation.status === 'completed') {
      if (operation.operation_type === 'purge') {
        message.success('Attendee data purged successfully')
        setPurgeSidebarOpen(false)
        setActiveOperationId(null)
        router.reload()
      } else {
        setActiveOperationId(null)
        window.location.href = '/admin/events'
      }
    } else if (operation.status === 'failed') {
      message.error(operation.error_message || 'The data operation failed')
      setActiveOperationId(null)
    }
  }, [operationQuery.data])

  const purgeDataMutation = useMutation({
    mutationFn: (fields: string[]) =>
      api.post<EventDataOperation, { fields: string[] }>(`/admin/events/${event.id}/purge`, {
        fields,
      }),
    onSuccess: (operation) => {
      message.success('Data purge started')
      setActiveOperationId(operation.id)
    },
    onError: (error: any) => {
      const errors = error.data?.errors
      message.error(errors?.join(', ') || error.message || 'Failed to start data purge')
    },
  })

  // Delete event mutation
  const deleteEventMutation = useMutation({
    mutationFn: (id: number) => {
      return api.delete<EventDataOperation>(`/admin/events/${id}`)
    },
    onSuccess: () => {
      message.success('Permanent event deletion started')
      setDeleteModalVisible(false)
      window.location.href = '/admin/events'
    },
    onError: (error: any) => {
      // Check if we have structured error messages from the backend
      if (error.data && error.data.errors) {
        const errorMessages = error.data.errors.join(', ')
        message.error(`Failed to delete event: ${errorMessages}`)
      } else {
        message.error(`Failed to delete event: ${error.message}`)
      }
      setDeleteModalVisible(false)
    },
  })

  // Handle back navigation
  const handleBack = () => {
    router.visit('/admin/events')
  }

  // Handle purge sidebar
  const handleOpenPurgeSidebar = () => {
    setPurgeSidebarOpen(true)
  }

  const handleClosePurgeSidebar = () => {
    setPurgeSidebarOpen(false)
  }

  const handlePurgeSubmit = async (selectedItems: string[]) => {
    await purgeDataMutation.mutateAsync(selectedItems)
  }

  // Handle delete event
  const handleDeleteEvent = () => {
    if (event?.id) {
      deleteEventMutation.mutate(event.id)
    }
  }

  const operationRunning = activeOperationId !== null

  // Get status badge color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'live':
        return '#52c41a'
      case 'draft':
        return '#d9d9d9'
      case 'archived':
        return '#1890ff'
      case 'purged':
        return '#ff7875'
      case 'deleted':
        return '#ff4d4f'
      default:
        return '#d9d9d9'
    }
  }

  const formatStatusLabel = (status: string) => {
    if (!status) return ''
    return status.charAt(0).toUpperCase() + status.slice(1)
  }

  const getApprovedPercentage = () => {
    const registered = event?.registered || 0
    const approved = event?.approved || 0
    if (registered <= 0 || approved <= 0) return 0
    return Math.round((approved / registered) * 100)
  }

  const getPendingApprovalPercentage = () => {
    const registered = event?.registered || 0
    const pending = event?.pending_approval || 0
    if (registered <= 0 || pending <= 0) return 0
    return Math.round((pending / registered) * 100)
  }

  if (!event) {
    return (
      <AdminLayout>
        <div className="text-red-500 text-center py-8">Error loading event details</div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <Head title="Event" />
      <div className="min-h-screen bg-[#f3f5f8]">
        {/* Header */}
        <div className="bg-white px-6 py-4 border-b border-gray-200">
          {/* Breadcrumb */}
          <div className="flex items-center text-sm mb-4">
            <span className="text-[rgba(0,0,0,0.45)]">List of Events</span>
            <span className="text-[rgba(0,0,0,0.45)] mx-2">/</span>
            <span className="text-[rgba(0,0,0,0.85)]">View</span>
          </div>

          {/* Page Title */}
          <div className="flex items-center">
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
              className="mr-3"
            />
            <Title level={4} className="!mb-0">
              {event.name}
            </Title>
          </div>
        </div>

        {/* Main Content */}
        <div className="p-6">
          {/* Enter Event Owner View Button */}
          <div className="flex justify-end mb-4">
            <Button
              type="primary"
              onClick={() => event.slug && router.visit(`/events/${event.slug}`)}
              disabled={!event.slug}
              className="!border !border-[#EB1964] !bg-[#EB1964] !shadow-[0_2px_0_0_rgba(0,0,0,0.04)] hover:!bg-[#d4175a] hover:!border-[#d4175a]"
              size="small"
            >
              Enter Event Owner View
            </Button>
          </div>

          {/* Statistics Container - Matches Figma Layout */}
          <div className="mb-6">
            {/* Top Statistics Card with Blue Border */}
            <div
              className="bg-white border-[3px] border-[#4277bb] mb-0"
              style={{ borderTopLeftRadius: '2px', borderTopRightRadius: '2px' }}
            >
              <div className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    {/* Event Title and Status */}
                    <div className="mb-4">
                      <div className="flex items-center mb-3">
                        <h1 className="text-[28px] font-bold text-[rgba(0,0,0,0.85)] leading-7 mr-4 font-['Roboto']">
                          {event.name}
                        </h1>
                        <div className="bg-white border border-transparent px-0 py-3 flex items-center">
                          <div
                            className="w-1.5 h-1.5 rounded-full mr-2"
                            style={{ backgroundColor: getStatusColor(event.status) }}
                          ></div>
                          <span className="text-sm font-normal text-[rgba(0,0,0,0.85)] font-['Roboto'] leading-[22px]">
                            {formatStatusLabel(event.status)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Statistics Row */}
                    <div className="flex gap-10">
                      {/* Registered */}
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1">
                          <span className="text-sm font-normal text-[rgba(0,0,0,0.45)] font-['Roboto'] leading-[22px]">
                            Registered
                          </span>
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <circle cx="7" cy="7" r="6.5" stroke="rgba(0,0,0,0.45)" />
                            <text
                              x="7"
                              y="9"
                              textAnchor="middle"
                              fontSize="8"
                              fill="rgba(0,0,0,0.45)"
                            >
                              i
                            </text>
                          </svg>
                        </div>
                        <div className="flex items-center gap-1 py-1.5">
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M13.9289 5.53153V4.4601C13.9289 4.36189 13.8485 4.28153 13.7503 4.28153H5.17885C5.08064 4.28153 5.00028 4.36189 5.00028 4.4601V5.53153C5.00028 5.62974 5.08064 5.7101 5.17885 5.7101H13.7503C13.8485 5.7101 13.9289 5.62974 13.9289 5.53153ZM5.17885 7.49581C5.08064 7.49581 5.00028 7.57617 5.00028 7.67439V8.74582C5.00028 8.84403 5.08064 8.92439 5.17885 8.92439H9.28599C9.38421 8.92439 9.46456 8.84403 9.46456 8.74582V7.67439C9.46456 7.57617 9.38421 7.49581 9.28599 7.49581H5.17885ZM13.5717 10.0851C10.9088 10.0851 8.75028 12.2436 8.75028 14.9065C8.75028 17.5695 10.9088 19.728 13.5717 19.728C16.2347 19.728 18.3931 17.5695 18.3931 14.9065C18.3931 12.2436 16.2347 10.0851 13.5717 10.0851ZM15.9713 17.3061C15.3306 17.9467 14.478 18.2994 13.5717 18.2994C12.6655 18.2994 11.8128 17.9467 11.1722 17.3061C10.5315 16.6655 10.1789 15.8128 10.1789 14.9065C10.1789 14.0003 10.5315 13.1476 11.1722 12.507C11.8128 11.8664 12.6655 11.5137 13.5717 11.5137C14.478 11.5137 15.3306 11.8664 15.9713 12.507C16.6119 13.1476 16.9646 14.0003 16.9646 14.9065C16.9646 15.8128 16.6119 16.6655 15.9713 17.3061ZM15.5583 13.2101H14.5695C14.5114 13.2101 14.4579 13.2369 14.4244 13.2838L13.007 15.2436L12.4914 14.5315C12.4748 14.5085 12.453 14.4898 12.4278 14.477C12.4025 14.4642 12.3746 14.4576 12.3463 14.4579H11.3619C11.2168 14.4579 11.132 14.623 11.2168 14.7414L12.8641 17.0204C12.9355 17.1186 13.0806 17.1186 13.1521 17.0204L15.7012 13.4936C15.7882 13.3753 15.7034 13.2101 15.5583 13.2101ZM8.39314 17.5851H3.21456V1.87081H15.7146V9.54939C15.7146 9.6476 15.7949 9.72796 15.8931 9.72796H17.1431C17.2414 9.72796 17.3217 9.6476 17.3217 9.54939V0.977958C17.3217 0.582868 17.0025 0.263672 16.6074 0.263672H2.32171C1.92662 0.263672 1.60742 0.582868 1.60742 0.977958V18.478C1.60742 18.873 1.92662 19.1922 2.32171 19.1922H8.39314C8.49135 19.1922 8.57171 19.1119 8.57171 19.0137V17.7637C8.57171 17.6655 8.49135 17.5851 8.39314 17.5851Z"
                              fill="rgba(0,0,0,0.85)"
                            />
                          </svg>
                          <span className="text-2xl font-semibold text-[rgba(0,0,0,0.85)] font-['Roboto'] leading-7">
                            {(event.registered || 0).toLocaleString()}
                          </span>
                        </div>
                      </div>

                      {/* Approved */}
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1">
                          <span className="text-sm font-normal text-[rgba(0,0,0,0.45)] font-['Roboto'] leading-[22px]">
                            Approved
                          </span>
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <circle cx="7" cy="7" r="6.5" stroke="rgba(0,0,0,0.45)" />
                            <text
                              x="7"
                              y="9"
                              textAnchor="middle"
                              fontSize="8"
                              fill="rgba(0,0,0,0.45)"
                            >
                              i
                            </text>
                          </svg>
                        </div>
                        <div className="flex items-center gap-1 py-1.5">
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M16.9696 14.1975C16.4109 13.6384 15.7581 13.182 15.0411 12.8493C16.0522 12.0301 16.6973 10.7801 16.6973 9.37831C16.6973 6.9051 14.6348 4.87608 12.1616 4.91403C9.72634 4.95198 7.76429 6.93635 7.76429 9.37831C7.76429 10.7801 8.41161 12.0301 9.42054 12.8493C8.70336 13.1817 8.05049 13.6381 7.49197 14.1975C6.27322 15.4185 5.58125 17.0301 5.53661 18.7488C5.53601 18.7727 5.54019 18.7964 5.5489 18.8186C5.5576 18.8407 5.57066 18.861 5.58731 18.878C5.60395 18.8951 5.62383 18.9086 5.6458 18.9179C5.66776 18.9271 5.69135 18.9319 5.71518 18.9319H6.96518C7.06116 18.9319 7.14152 18.856 7.14375 18.76C7.18616 17.4654 7.71072 16.2533 8.63259 15.3337C9.10433 14.8594 9.66545 14.4834 10.2835 14.2275C10.9015 13.9716 11.5641 13.8407 12.233 13.8426C13.5924 13.8426 14.8714 14.3716 15.8335 15.3337C16.7531 16.2533 17.2777 17.4654 17.3223 18.76C17.3246 18.856 17.4049 18.9319 17.5009 18.9319H18.7509C18.7747 18.9319 18.7983 18.9271 18.8203 18.9179C18.8422 18.9086 18.8621 18.8951 18.8788 18.878C18.8954 18.861 18.9085 18.8407 18.9172 18.8186C18.9259 18.7964 18.9301 18.7727 18.9295 18.7488C18.8848 17.0301 18.1929 15.4185 16.9696 14.1975ZM12.233 12.2355C11.4696 12.2355 10.7509 11.9386 10.2129 11.3984C9.94298 11.1306 9.72972 10.8111 9.58591 10.459C9.4421 10.107 9.37067 9.72956 9.37589 9.3493C9.38259 8.61715 9.675 7.90956 10.1862 7.38501C10.7219 6.8359 11.4384 6.5301 12.204 6.52117C12.9607 6.51448 13.6951 6.80912 14.2353 7.33814C14.7888 7.88055 15.0924 8.60599 15.0924 9.37831C15.0924 10.1417 14.7955 10.8582 14.2554 11.3984C13.9903 11.6647 13.6752 11.8758 13.328 12.0195C12.9809 12.1631 12.6087 12.2366 12.233 12.2355ZM6.64152 9.9676C6.62143 9.7734 6.61027 9.57698 6.61027 9.37831C6.61027 9.0234 6.64375 8.67742 6.70625 8.34037C6.72188 8.26001 6.67946 8.17742 6.6058 8.14394C6.30223 8.00778 6.02321 7.82028 5.78214 7.58367C5.49808 7.30824 5.27455 6.97662 5.12581 6.60997C4.97708 6.24332 4.90641 5.8497 4.9183 5.45421C4.93839 4.73769 5.22634 4.05689 5.72857 3.54349C6.27991 2.97876 7.02098 2.67072 7.80893 2.67965C8.52098 2.68635 9.20848 2.9609 9.72857 3.44751C9.90491 3.61269 10.0567 3.79572 10.1839 3.99215C10.2286 4.06135 10.3156 4.09037 10.3915 4.06358C10.7844 3.92742 11.1996 3.83144 11.6259 3.7868C11.7509 3.7734 11.8223 3.63947 11.7665 3.52787C11.0411 2.0926 9.55893 1.10153 7.84464 1.07474C5.3692 1.0368 3.3067 3.06581 3.3067 5.5368C3.3067 6.93858 3.95179 8.18858 4.96295 9.00778C4.25313 9.3359 3.59911 9.78903 3.03214 10.356C1.80893 11.577 1.11696 13.1886 1.07232 14.9096C1.07173 14.9334 1.0759 14.9571 1.08461 14.9793C1.09332 15.0015 1.10638 15.0217 1.12302 15.0387C1.13966 15.0558 1.15955 15.0693 1.18151 15.0786C1.20347 15.0878 1.22706 15.0926 1.25089 15.0926H2.50313C2.59911 15.0926 2.67946 15.0167 2.6817 14.9207C2.72411 13.6261 3.24866 12.414 4.17054 11.4944C4.82679 10.8381 5.63036 10.3828 6.50759 10.1618C6.59464 10.1395 6.65268 10.0569 6.64152 9.9676Z"
                              fill="rgba(0,0,0,0.85)"
                            />
                          </svg>
                          <span className="text-2xl font-semibold text-[rgba(0,0,0,0.85)] font-['Roboto'] leading-7 mr-1">
                            {(event.approved || 0).toLocaleString()}
                          </span>
                          <span className="text-xs font-normal text-[#52c41a] font-['Roboto'] leading-5">
                            {getApprovedPercentage()}%
                          </span>
                        </div>
                      </div>

                      {/* Pending Approval */}
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1">
                          <span className="text-sm font-normal text-[rgba(0,0,0,0.45)] font-['Roboto'] leading-[22px]">
                            Pending Approval
                          </span>
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <circle cx="7" cy="7" r="6.5" stroke="rgba(0,0,0,0.45)" />
                            <text
                              x="7"
                              y="9"
                              textAnchor="middle"
                              fontSize="8"
                              fill="rgba(0,0,0,0.45)"
                            >
                              i
                            </text>
                          </svg>
                        </div>
                        <div className="flex items-center gap-1 py-1.5">
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M19.6636 7.76909H13.9493C13.8511 7.76909 13.7707 7.84945 13.7707 7.94767V9.01909C13.7707 9.11731 13.8511 9.19767 13.9493 9.19767H19.6636C19.7618 9.19767 19.8422 9.11731 19.8422 9.01909V7.94767C19.8422 7.84945 19.7618 7.76909 19.6636 7.76909ZM16.6725 10.8048H13.9493C13.8511 10.8048 13.7707 10.8852 13.7707 10.9834V12.0548C13.7707 12.153 13.8511 12.2334 13.9493 12.2334H16.6725C16.7707 12.2334 16.8511 12.153 16.8511 12.0548V10.9834C16.8511 10.8852 16.7707 10.8048 16.6725 10.8048ZM9.22386 5.77133H8.25735C8.11895 5.77133 8.00735 5.88293 8.00735 6.02133V11.557C8.00735 11.6374 8.04529 11.7111 8.11002 11.7579L11.4337 14.182C11.5453 14.2624 11.7015 14.2401 11.7819 14.1285L12.3556 13.345V13.3428C12.4359 13.2311 12.4114 13.0749 12.2998 12.9945L9.47163 10.9499V6.02133C9.47386 5.88293 9.36003 5.77133 9.22386 5.77133Z"
                              fill="rgba(0,0,0,0.85)"
                            />
                            <path
                              d="M16.5349 13.6095H15.2447C15.1197 13.6095 15.0014 13.6742 14.9344 13.7814C14.6505 14.2314 14.3174 14.6484 13.9411 15.0247C13.2933 15.6746 12.5254 16.1924 11.68 16.5492C10.8028 16.9198 9.87195 17.1073 8.91213 17.1073C7.95007 17.1073 7.01927 16.9198 6.14427 16.5492C5.29829 16.1921 4.53713 15.6787 3.88311 15.0247C3.22909 14.3707 2.7157 13.6095 2.35855 12.7635C1.98802 11.8885 1.80052 10.9577 1.80052 9.99566C1.80052 9.03361 1.98802 8.10503 2.35855 7.2278C2.7157 6.38182 3.22909 5.62066 3.88311 4.96664C4.53713 4.31262 5.29829 3.79923 6.14427 3.44209C7.01927 3.07155 7.9523 2.88405 8.91213 2.88405C9.87418 2.88405 10.805 3.07155 11.68 3.44209C12.526 3.79923 13.2871 4.31262 13.9411 4.96664C14.3174 5.34291 14.6505 5.75991 14.9344 6.20994C15.0014 6.31709 15.1197 6.38182 15.2447 6.38182H16.5349C16.6889 6.38182 16.7871 6.22111 16.7179 6.08494C15.2626 3.18986 12.3117 1.30816 9.01704 1.27021C4.19337 1.20994 0.186678 5.15861 0.17775 9.9778C0.168821 14.8059 4.08177 18.7233 8.90989 18.7233C12.2469 18.7233 15.2469 16.8349 16.7179 13.9064C16.7336 13.8751 16.7411 13.8404 16.7395 13.8055C16.738 13.7706 16.7276 13.7366 16.7092 13.7069C16.6909 13.6771 16.6652 13.6526 16.6347 13.6355C16.6042 13.6185 16.5698 13.6095 16.5349 13.6095Z"
                              fill="rgba(0,0,0,0.85)"
                            />
                          </svg>
                          <span className="text-2xl font-semibold text-[rgba(0,0,0,0.85)] font-['Roboto'] leading-7 mr-1">
                            {(event.pending_approval || 0).toLocaleString()}
                          </span>
                          <span className="text-xs font-normal text-[#faad14] font-['Roboto'] leading-5">
                            {getPendingApprovalPercentage()}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Event Logo */}
                  {event.logo && (
                    <div className="w-20 h-20 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
                      <img src={event.logo} alt={`${event.name} logo`} className="w-full h-full object-contain" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Bottom Scan Points Section with Light Border */}
            <div
              className="bg-white border border-[#ebebeb] mt-[20px]"
              style={{ borderBottomLeftRadius: '2px', borderBottomRightRadius: '2px' }}
            >
              <div className="px-6 py-4">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <h2 className="text-base font-bold text-[rgba(0,0,0,0.85)] font-['Roboto'] leading-[28px] mb-0">
                      Number of attendees for each Scan Point
                    </h2>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <circle cx="7" cy="7" r="6.5" stroke="rgba(0,0,0,0.45)" />
                      <text x="7" y="9" textAnchor="middle" fontSize="8" fill="rgba(0,0,0,0.45)">
                        i
                      </text>
                    </svg>
                </div>
                <span className="text-xs text-[rgba(0,0,0,0.85)] font-['Roboto'] leading-[20px]">
                  Last Updated:{' '}
                  {event.scan_points_last_scanned_at
                    ? dayjs(event.scan_points_last_scanned_at).format('M/D/YY')
                    : '—'}
                </span>
              </div>

              <div className="flex flex-wrap justify-start" style={{ gap: '72px' }}>
                {(event.scan_points || []).map((sp) => (
                  <div key={sp.id} className="text-center">
                    <div className="text-sm font-normal text-[rgba(0,0,0,0.45)] font-['Roboto'] leading-[22px] mb-1">
                      {sp.name}
                    </div>
                    <div className="text-2xl font-normal text-[rgba(0,0,0,0.85)] font-['Roboto'] leading-[32px]">
                      {sp.count.toLocaleString()}
                    </div>
                  </div>
                ))}
                {(event.scan_points || []).length === 0 && (
                  <div className="text-sm text-[rgba(0,0,0,0.45)]">No scan points.</div>
                )}
              </div>
            </div>
          </div>
        </div>

          {/* Event Details */}
          <EventDetailsSection
            eventName={event.name}
            startDate={event.start_date}
            endDate={event.end_date}
            themeColor={event.theme}
            logoUrl={event.logo || undefined}
            allowPublicRegistration={event.allow_public_registration}
            allowAttendeeAppAccess={event.allow_attendee_app_access}
            clientName={event.client_name}
          />

          {/* Action Cards */}
          <div className="flex gap-6">
            <ActionCard
              title="Purge Data"
              description="Purging replaces attendee sensitive personal data, including full name, with masked values ('**') to comply with data retention policies."
              buttonText="Purge Data"
              buttonClassName="bg-[#132b51] text-white px-4 py-1.5 rounded-sm border border-[#132b51] shadow-[0px_2px_0px_0px_rgba(0,0,0,0.04)] hover:bg-[#0f1f3c] transition-colors cursor-pointer"
              onClick={handleOpenPurgeSidebar}
              disabled={operationRunning || event.status === 'purged'}
            />

            <ActionCard
              title="Permanently Delete Event"
              description="Permanently delete this event and all associated data. This action cannot be undone. All event details, attendee records, and reports will be permanently removed from the system."
              buttonText="Delete Event"
              buttonClassName="text-white px-4 py-1.5 rounded-sm border border-[#6e1736] shadow-[0px_2px_0px_0px_rgba(0,0,0,0.04)] cursor-pointer"
              buttonStyle={{
                background:
                  'linear-gradient(90deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.2) 100%), linear-gradient(90deg, rgb(137, 29, 68) 0%, rgb(137, 29, 68) 100%)',
              }}
              onClick={() => setDeleteModalVisible(true)}
              disabled={operationRunning}
            />
          </div>
        </div>

        {/* Delete Event Modal */}
        <DeleteEventModal
          visible={deleteModalVisible}
          eventName={event.name}
          isDeleting={deleteEventMutation.isPending}
          onConfirm={handleDeleteEvent}
          onCancel={() => setDeleteModalVisible(false)}
        />

        {/* Purge Data Sidebar */}
        <PurgeDataSidebar
          isOpen={purgeSidebarOpen}
          fields={purgeFields}
          isSubmitting={purgeDataMutation.isPending || operationRunning}
          onClose={handleClosePurgeSidebar}
          onSubmit={handlePurgeSubmit}
        />
      </div>
    </AdminLayout>
  )
}
